/**
 * User types — what an account is allowed to do.
 *
 * The screen is built around one fact that people get wrong: a user type is
 * not a label, it is a set of permissions, and everybody already on it gets
 * whatever you tick the moment you save. So the count of people affected sits
 * next to the save button, and saving says how many.
 *
 * The other thing it does deliberately: a permission you do not hold yourself
 * is shown but cannot be ticked, with the reason on it. Hiding it would leave
 * somebody hunting for a permission they can see in the documentation and not
 * on the screen; offering it would mean a button that refuses.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '../../api';

type Node = { key: string; label: string; note?: string; children?: Node[] };
type UserType = {
  id: number; name: string; defaultRole: 'administrator' | 'instructor' | 'learner';
  isDefault: boolean; permissions: string[]; roles: string[];
  holders: number; withinYourRank: boolean;
};
type Payload = { types: UserType[]; catalogue: Node[]; yours: string[] };

const ROLES = ['administrator', 'instructor', 'learner'] as const;

export function AdminUserTypes() {
  const [data, setData] = useState<Payload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<number | 'new' | null>(null);

  const load = useCallback(async () => {
    try { setData(await api.get<Payload>('/admin/user-types')); }
    catch (e) { setError((e as ApiError).message); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const act = async (fn: () => Promise<unknown>, said: string) => {
    setBusy(true); setError(null); setNote(null);
    try { await fn(); setNote(said); setEditing(null); await load(); }
    catch (e) { setError((e as ApiError).message); }
    finally { setBusy(false); }
  };

  if (!data) {
    return <><h1>User types</h1>{error ? <p className="auth__error">{error}</p> : <p className="muted">Loading…</p>}</>;
  }

  const current = typeof editing === 'number' ? data.types.find((t) => t.id === editing) : null;

  return (
    <>
      <h1>User types</h1>
      <p className="muted">
        What an account is allowed to do. Everybody on a type gets whatever it grants, the moment it
        is saved — so changing one changes what those people can do straight away.
      </p>

      {error && <p className="auth__error">{error}</p>}
      {note && <p className="notice">{note}</p>}

      <table className="grid">
        <thead>
          <tr><th>Type</th><th>Signs in as</th><th>People</th><th>Can</th><th /></tr>
        </thead>
        <tbody>
          {data.types.map((t) => (
            <tr key={t.id}>
              <td>
                <strong>{t.name}</strong>
                {t.isDefault && <span className="pill pill--soft"> new accounts</span>}
              </td>
              <td className="small">{t.defaultRole}</td>
              <td className="num">{t.holders}</td>
              <td className="small muted">{t.roles.join(', ') || 'nothing'}</td>
              <td className="row-actions">
                {t.withinYourRank ? (
                  <>
                    <button type="button" className="linkish" onClick={() => setEditing(t.id)}>edit</button>
                    {!t.isDefault && (
                      <button type="button" className="linkish" disabled={busy}
                        onClick={() => void act(
                          () => api.post(`/admin/user-types/${t.id}/default`),
                          `New accounts will be ${t.name}.`)}>
                        make default
                      </button>
                    )}
                    {!t.isDefault && t.holders === 0 && (
                      <button type="button" className="linkish" disabled={busy}
                        onClick={() => {
                          if (!confirm(`Remove the “${t.name}” user type?`)) return;
                          void act(() => api.del(`/admin/user-types/${t.id}`), 'User type removed.');
                        }}>
                        remove
                      </button>
                    )}
                  </>
                ) : (
                  <span className="muted small">can do more than you</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <p>
        <button type="button" disabled={busy} onClick={() => setEditing('new')}>New user type</button>
      </p>

      {editing !== null && (
        <Editor
          key={editing === 'new' ? 'new' : current?.id}
          type={current ?? null}
          catalogue={data.catalogue}
          yours={data.yours}
          busy={busy}
          onCancel={() => setEditing(null)}
          onSave={(body) => act(
            () => current
              ? api.patch(`/admin/user-types/${current.id}`, body)
              : api.post('/admin/user-types', body),
            current
              ? `Saved. ${current.holders} ${current.holders === 1 ? 'person' : 'people'} on this type ${current.holders === 1 ? 'is' : 'are'} affected.`
              : 'User type created.',
          )}
        />
      )}
    </>
  );
}

function Editor({ type, catalogue, yours, busy, onSave, onCancel }: {
  type: UserType | null;
  catalogue: Node[];
  yours: string[];
  busy: boolean;
  onSave: (body: { name: string; defaultRole: string; permissions: string[] }) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(type?.name ?? '');
  const [defaultRole, setRole] = useState<string>(type?.defaultRole ?? 'learner');
  const [picked, setPicked] = useState<Set<string>>(new Set(type?.permissions ?? []));

  const mayTick = useMemo(() => new Set(yours), [yours]);

  const toggle = (key: string, on: boolean) => {
    setPicked((was) => {
      const next = new Set(was);
      if (on) next.add(key); else next.delete(key);
      return next;
    });
  };

  // Which roles this selection actually reaches — the server refuses a type
  // that claims a role nothing in it grants, so the form says so first.
  const reaches = useMemo(() => {
    const out = new Set<string>();
    for (const k of picked) {
      for (const r of ROLES) if (k === r || k.startsWith(`${r}.`)) out.add(r);
    }
    return out;
  }, [picked]);

  const roleOk = reaches.has(defaultRole);
  const canSave = name.trim() !== '' && picked.size > 0 && roleOk;

  return (
    <section className="panel">
      <h2 className="h2">{type ? `Editing ${type.name}` : 'New user type'}</h2>

      {type && type.holders > 0 && (
        <p className="notice notice--warn">
          {type.holders} {type.holders === 1 ? 'person is' : 'people are'} on this type. Whatever you
          change here changes what {type.holders === 1 ? 'they' : 'they'} can do as soon as you save.
        </p>
      )}

      <div className="row">
        <label>
          <span>Name</span>
          <input value={name} maxLength={100} onChange={(e) => setName(e.target.value)} />
        </label>
        <label>
          <span>Signs in as</span>
          <select value={defaultRole} onChange={(e) => setRole(e.target.value)}>
            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </label>
      </div>
      {!roleOk && picked.size > 0 && (
        <p className="warn small">
          Nothing ticked below grants {defaultRole} access, so this type cannot start there.
        </p>
      )}

      <div className="perms">
        {catalogue.map((root) => (
          <fieldset key={root.key} className="perms__group">
            <legend>{root.label}</legend>
            {root.note && <p className="muted small">{root.note}</p>}

            <Tick node={root} picked={picked} mayTick={mayTick} onToggle={toggle} whole />
            <div className="perms__children">
              {root.children?.map((c) => (
                <Tick key={c.key} node={c} picked={picked} mayTick={mayTick} onToggle={toggle}
                  coveredBy={picked.has(root.key) ? root.label : undefined} />
              ))}
            </div>
          </fieldset>
        ))}
      </div>

      <div className="row">
        <button type="button" disabled={busy || !canSave}
          onClick={() => onSave({ name: name.trim(), defaultRole, permissions: [...picked] })}>
          {busy ? 'Saving…' : type ? 'Save' : 'Create'}
        </button>
        <button type="button" className="btn-danger" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </section>
  );
}

function Tick({ node, picked, mayTick, onToggle, whole, coveredBy }: {
  node: Node;
  picked: Set<string>;
  mayTick: Set<string>;
  onToggle: (key: string, on: boolean) => void;
  whole?: boolean;
  coveredBy?: string;
}) {
  const allowed = mayTick.has(node.key);
  const on = picked.has(node.key) || !!coveredBy;

  return (
    <label className={`check perms__row${allowed ? '' : ' is-locked'}`}>
      <input
        type="checkbox" checked={on}
        disabled={!allowed || !!coveredBy}
        onChange={(e) => onToggle(node.key, e.target.checked)}
      />
      <span>
        {whole ? <strong>Everything under {node.label}</strong> : node.label}
        {!whole && node.note && <span className="muted small"> — {node.note}</span>}
        {coveredBy && <span className="muted small"> — included in “Everything under {coveredBy}”</span>}
        {!allowed && <span className="warn small"> — you do not have this yourself</span>}
      </span>
    </label>
  );
}
